// Weeks run from reset to reset (SPEC §6.11). The reset is a setting, Wednesday 04:00 UTC by
// default. A week's id is the UTC date its reset falls on: "2026-11-18".

/** Weekday 0–6 (Sunday = 0) and hour 0–23, both UTC. */
export interface ResetSchedule {
  weekday: number;
  hourUtc: number;
}

// ASSUMPTION(forever): A4
export const DEFAULT_RESET: ResetSchedule = { weekday: 3, hourUtc: 4 };

const HOUR = 3_600_000;
const DAY = 24 * HOUR;
export const WEEK_MS = 7 * DAY;

/** The reset that starts the week `at` falls in. */
export function weekStart(at: Date, reset: ResetSchedule = DEFAULT_RESET): Date {
  const day = new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate()));
  const back = (day.getUTCDay() - reset.weekday + 7) % 7;
  let start = day.getTime() - back * DAY + reset.hourUtc * HOUR;
  if (start > at.getTime()) start -= WEEK_MS;
  return new Date(start);
}

/** "2026-11-18": the UTC date of the week's reset. */
export function weekIdOf(at: Date, reset: ResetSchedule = DEFAULT_RESET): string {
  return weekStart(at, reset).toISOString().slice(0, 10);
}

/** The week with this id, or null when the id isn't a reset date under this schedule. */
export function weekRange(
  id: string,
  reset: ResetSchedule = DEFAULT_RESET,
): { start: Date; end: Date } | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(id)) return null;
  const start = new Date(`${id}T${String(reset.hourUtc).padStart(2, '0')}:00:00Z`);
  if (Number.isNaN(start.getTime()) || start.getUTCDay() !== reset.weekday) return null;
  return { start, end: new Date(start.getTime() + WEEK_MS) };
}

/** A span cut at each reset: the seconds that fall in each week it touches, by week id. */
export function splitByWeek(
  start: Date,
  end: Date,
  reset: ResetSchedule = DEFAULT_RESET,
): { weekId: string; seconds: number }[] {
  const out: { weekId: string; seconds: number }[] = [];
  let from = start.getTime();
  const to = end.getTime();
  while (from < to) {
    const weekBegins = weekStart(new Date(from), reset).getTime();
    const cut = Math.min(to, weekBegins + WEEK_MS);
    out.push({
      weekId: new Date(weekBegins).toISOString().slice(0, 10),
      seconds: (cut - from) / 1000,
    });
    from = cut;
  }
  return out;
}
